import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';

import { CONTENT_PREFERENCE_CRITERIA } from 'bdvp-advanced-search/src/utils/constants';

import { useTopic, useSearchQueryPlayback } from '../hooks/store';
import {
    useDefaultTopic,
    useTeamId,
    useIsClubPage,
    useMediaConfig,
} from '../hooks/config';
import {
    QUERY_PARAMS,
    SEARCH_ROUTE,
    CarouselRowType,
} from '../utils/constants';

import Component from '../components/CarouselRow';

const LIMIT = 36;

const CarouselRow = forwardRef(({
    skip,
    ssr,
    properties,
    rank,
    className,
    type,
    applyClubRenderConditions,
}, ref) => {

    const defaultTopicId = useDefaultTopic();
    const teamId = useTeamId();
    const isClubPage = useIsClubPage();
    const { mlbDefaultTopicId = 'most-popular' } = useMediaConfig();

    let {
        title: carouselTitle,
        query: searchQuery,
        queryClubFilter: clubFilter,
        onItemClickHistoryPushState,
        topic: topicId,
    } = properties;

    const isSearchData = !!searchQuery;
    const useData = isSearchData ? useSearchQueryPlayback : useTopic;
    const options = {
        ssr,
        limit: LIMIT,
        skip,
    };

    let id = topicId === mlbDefaultTopicId ? defaultTopicId : topicId || defaultTopicId;
    let link = `/topic/${id}`;
    let searchParams = `?${QUERY_PARAMS.topicId}=${id}`;

    if (isSearchData) {

        if (isClubPage && clubFilter) {

            searchQuery = `${clubFilter} == [${teamId}] AND ${searchQuery}`;

        }
        id = searchQuery;
        options.contentPreference = CONTENT_PREFERENCE_CRITERIA.mixed;
        searchParams = `?${QUERY_PARAMS.query}=${searchQuery}&${QUERY_PARAMS.playlistTitle}=${carouselTitle}`;
        link = `${SEARCH_ROUTE}?${QUERY_PARAMS.query}=${searchQuery}`;

    }

    // https://baseball.atlassian.net/browse/BDVP-2972 don't render Carousel in club pages conditions
    let shouldReturnEmptyRender = false;

    if (applyClubRenderConditions && isClubPage) {

        if (isSearchData && !clubFilter) {

            shouldReturnEmptyRender = true;

        }

        if (!isSearchData && id !== defaultTopicId) {

            shouldReturnEmptyRender = true;

        }

    }

    if (shouldReturnEmptyRender) {

        options.skip = true;

    }

    const {
        loading,
        loadingMore,
        data,
        fetchMoreData,
    } = useData(id, options);

    if (shouldReturnEmptyRender) {

        return null;

    }

    const title = isSearchData ? carouselTitle : data?.topicPlayList?.title ?? '';
    const list = data?.topicPlayList?.mediaPlayback ?? [];
    const hasMore = data?.topicPlayList?.hasMore ?? false;

    return (
        <Component
            ref={ref}
            className={className}
            title={title}
            list={list}
            hasMore={hasMore}
            fetchMoreData={fetchMoreData}
            isLoading={skip || loading}
            isLoadingMore={loadingMore}
            limit={LIMIT}
            link={link}
            searchParams={searchParams}
            rank={rank}
            isSearchData={isSearchData}
            onItemClickHistoryPushState={onItemClickHistoryPushState}
            type={type}
        />
    );

});

CarouselRow.propTypes = {
    rank: PropTypes.number.isRequired,
    skip: PropTypes.bool,
    ssr: PropTypes.bool,
    properties: PropTypes.object,
    type: PropTypes.string,
    applyClubRenderConditions: PropTypes.bool,
};

CarouselRow.defaultProps = {
    skip: false,
    ssr: true,
    properties: {},
    type: CarouselRowType.GENERIC,
    applyClubRenderConditions: false,
};

export default CarouselRow;
